'use client';

import { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import QuickAccessCards from './QuickAccessCards';
import FAQSection from './FAQSection';
import ResourceLibrary from './ResourceLibrary';
import ContactOptions from './ContactOptions';
import TroubleshootingWizard from './TroubleshootingWizard';
import CommunityForum from './CommunityForum';

const SupportCenterInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const quickAccessCards = [
  {
    id: 1,
    title: 'Shipping Guides',
    description: 'Comprehensive guide for all local and international shipping services',
    icon: 'BookOpenIcon',
    color: 'bg-primary',
    link: '/shipping-guides'
  },
  {
    id: 2,
    title: 'Customs Documentation',
    description: 'Customs requirements and documents needed for international shipping',
    icon: 'DocumentCheckIcon',
    color: 'bg-secondary',
    link: '/customs-docs'
  },
  {
    id: 3,
    title: 'Packaging Tutorials',
    description: 'Best practices for safely packing and wrapping shipments',
    icon: 'CubeIcon',
    color: 'bg-accent',
    link: '/packaging-tutorials'
  },
  {
    id: 4,
    title: 'Prohibited Items',
    description: 'List of prohibited and restricted items in shipping',
    icon: 'ExclamationTriangleIcon',
    color: 'bg-warning',
    link: '/restricted-items'
  }];


  const faqCategories = ['All', 'Shipping', 'Tracking', 'Billing', 'Customs', 'Delivery'];

  const faqs = [
  {
    id: 1,
    question: 'How can I track my shipment?',
    answer: 'You can easily track your shipment by entering the tracking number on the advanced tracking page. You will get instant updates about your shipment location and expected delivery time. You can also enable instant notifications to receive automatic updates via email or text messages.',
    category: 'Tracking'
  },
  {
    id: 2,
    question: 'What documents are required for international shipping?',
    answer: 'For international shipping, you will need: commercial invoice, packing list, certificate of origin (if required), insurance documents, and any special permits depending on the type of goods. We provide ready-made templates for all these documents in the resources section.',
    category: 'Customs'
  },
  {
    id: 3,
    question: 'How is shipping cost calculated?',
    answer: 'Shipping cost depends on several factors: actual weight or volumetric weight (whichever is greater), distance between shipping point and destination, type of service selected (standard, express, or urgent), and additional services such as insurance or door-to-door delivery. Use the shipping calculator for an instant estimate.',
    category: 'Billing'
  },
  {
    id: 4,
    question: 'What should I do if my shipment is delayed?',
    answer: 'If your shipment is delayed beyond the expected date, first check the tracking page for the latest update. In case of unexplained delay, contact the support team via live chat or phone. We provide compensation in cases of confirmed delays according to service terms.',
    category: 'Delivery'
  },
  {
    id: 5,
    question: 'Can I change the delivery address after shipping?',
    answer: 'Yes, you can change the delivery address before the shipment reaches the final distribution center. Use the "Modify Delivery" feature on the tracking page or contact customer service. Additional fees may apply depending on the new distance.',
    category: 'Shipping'
  },
  {
    id: 6,
    question: 'How do I pack fragile items?',
    answer: 'For fragile items, use a strong box with double layer, fill gaps with cushioning materials (bubble wrap or foam), place a clear "Fragile" label on all sides of the box, and choose appropriate shipping service with insurance. Review the comprehensive packaging guide for detailed instructions.',
    category: 'Shipping'
  }];


  const resources = [
  {
    id: 1,
    title: 'Comprehensive International Shipping Guide',
    description: 'Everything you need to know about international shipping from start to finish',
    type: 'Guide',
    duration: '15 minute read',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_135ea089a-1764637482598.png",
    thumbnailAlt: 'Professional logistics manager reviewing international shipping documents on tablet in modern warehouse with cargo containers',
    downloadUrl: '/resources/international-shipping-guide.pdf'
  },
  {
    id: 2,
    title: 'How to Pack Shipments Professionally',
    description: 'Tutorial video showing best packing and wrapping methods',
    type: 'Video',
    duration: '8 minutes',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16885cae6-1767260679136.png",
    thumbnailAlt: 'Warehouse worker in safety vest carefully packing fragile items with bubble wrap in cardboard box',
    downloadUrl: '/resources/packaging-tutorial-video'
  },
  {
    id: 3,
    title: 'Webinar: Optimizing Shipping Costs',
    description: 'Practical strategies to reduce shipping costs and increase efficiency',
    type: 'Webinar',
    duration: '45 minutes',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1abdc0f87-1767521790771.png",
    thumbnailAlt: 'Business presentation showing shipping cost optimization charts and graphs on large screen in conference room',
    downloadUrl: '/resources/cost-optimization-webinar'
  },
  {
    id: 4,
    title: 'Customs Procedures Guide',
    description: 'Detailed explanation of customs procedures and required documents',
    type: 'Guide',
    duration: '20 minute read',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_19126c266-1764670092970.png",
    thumbnailAlt: 'Customs officer examining shipping documents and cargo manifest at international border checkpoint',
    downloadUrl: '/resources/customs-procedures-guide.pdf'
  },
  {
    id: 5,
    title: 'Using the Advanced Tracking System',
    description: 'Visual guide to using all tracking system features',
    type: 'Video',
    duration: '12 minutes',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c950f92c-1764653670254.png",
    thumbnailAlt: 'Computer screen displaying advanced shipment tracking dashboard with real-time GPS location and delivery status',
    downloadUrl: '/resources/tracking-system-tutorial'
  },
  {
    id: 6,
    title: 'Webinar: E-commerce Shipping',
    description: 'Integrated shipping solutions for e-commerce store owners',
    type: 'Webinar',
    duration: '60 minutes',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18c633d5a-1766839954925.png",
    thumbnailAlt: 'E-commerce business owner managing online orders and shipping logistics on laptop in modern office',
    downloadUrl: '/resources/ecommerce-shipping-webinar'
  }];


  const contactOptions = [
  {
    id: 1,
    title: 'Live Chat',
    description: 'Talk to a support expert instantly',
    icon: 'ChatBubbleLeftRightIcon',
    action: 'Start Chat',
    availability: 'Available 24/7',
    color: 'bg-primary'
  },
  {
    id: 2,
    title: 'Phone Support',
    description: 'Call us for immediate assistance',
    icon: 'PhoneIcon',
    action: 'Call Now',
    availability: 'Available 24/7',
    color: 'bg-secondary'
  },
  {
    id: 3,
    title: 'Email',
    description: 'Send your inquiry and we will respond within two hours',
    icon: 'EnvelopeIcon',
    action: 'Send Message',
    availability: 'Response within 2 hours',
    color: 'bg-accent'
  },
  {
    id: 4,
    title: 'WhatsApp',
    description: 'Contact us via WhatsApp',
    icon: 'DevicePhoneMobileIcon',
    action: 'Open WhatsApp',
    availability: 'Available 24/7',
    color: 'bg-success'
  }];


  const wizardSteps = [
  {
    id: 1,
    question: 'What problem are you facing?',
    options: [
    { id: 1, text: 'Tracking issue', nextStep: 2, solution: null },
    { id: 2, text: 'Delivery delay', nextStep: 3, solution: null },
    { id: 3, text: 'Billing issue', nextStep: 4, solution: null },
    { id: 4, text: 'Packaging issue', nextStep: null, solution: 'Review the comprehensive packaging guide in the educational resources section. If the shipment has already been sent, contact the support team to assess the situation and provide appropriate solutions.' }]

  },
  {
    id: 2,
    question: 'What type of tracking issue?',
    options: [
    { id: 1, text: 'Tracking number not working', nextStep: null, solution: 'Make sure to enter the tracking number correctly without spaces. If the problem persists, the system may need time to update data (up to 24 hours). Contact support if the problem continues after 24 hours.' },
    { id: 2, text: 'Tracking not updated for a while', nextStep: null, solution: 'The shipment may be in transit between centers. Check the last recorded location. If more than 48 hours have passed without an update, contact the support team for a status update.' },
    { id: 3, text: 'Tracking information unclear', nextStep: null, solution: 'Review the tracking codes guide in the help section. Each status code has a specific meaning. If you need additional clarification, contact the support team.' }]

  },
  {
    id: 3,
    question: 'How long is the delay?',
    options: [
    { id: 1, text: 'Less than 24 hours', nextStep: null, solution: 'Minor delays are normal due to logistical conditions. Follow tracking for updates. If the delay exceeds 24 hours, contact us.' },
    { id: 2, text: '1-3 days', nextStep: null, solution: 'We apologize for the delay. Contact the support team immediately to find out the reason and get an updated delivery date. You may be eligible for compensation according to service terms.' },
    { id: 3, text: 'More than 3 days', nextStep: null, solution: 'This is an unacceptable delay. Contact the support team immediately to open an urgent investigation. You will receive daily updates and appropriate compensation according to company policy.' }]

  },
  {
    id: 4,
    question: 'What type of billing issue?',
    options: [
    { id: 1, text: 'Unexpected charges', nextStep: null, solution: 'Review invoice details in your account. There may be additional charges for extra services or customs fees. Contact the billing department for a detailed explanation.' },
    { id: 2, text: 'Amount error', nextStep: null, solution: 'Contact the billing department immediately with the invoice number. The invoice will be reviewed and any errors corrected within 24 hours.' },
    { id: 3, text: 'Refund request', nextStep: null, solution: 'Submit a refund request through your account with an explanation of the reason. The request will be reviewed within 3-5 business days. Approved refunds are processed within 7-10 business days.' }]

  }];


  const forumPosts = [
  {
    id: 1,
    title: 'Best way to pack electronics for international shipping',
    excerpt: 'I share with you my experience in shipping valuable electronics internationally. I used the double packing method with shock-absorbing materials...',
    author: 'Ahmed Mohamed',
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_19458974c-1763300956934.png",
    authorImageAlt: 'Professional Middle Eastern businessman in navy suit smiling confidently in modern office',
    replies: 24,
    views: 342,
    category: 'Packaging',
    timestamp: '3 hours ago',
    isSolved: true
  },
  {
    id: 2,
    title: 'How I avoided additional customs fees on my last shipment',
    excerpt: 'After several experiences with international shipping, I learned the importance of correctly declaring the value of goods. Here are some tips...',
    author: 'Fatima Ali',
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1459b9ea0-1763295924782.png",
    authorImageAlt: 'Professional Arab businesswoman wearing hijab and formal attire smiling in corporate office setting',
    replies: 18,
    views: 256,
    category: 'Customs',
    timestamp: '5 hours ago',
    isSolved: false
  },
  {
    id: 3,
    title: 'My experience with express shipping for sensitive documents',
    excerpt: 'I needed to ship important documents very quickly. I used the urgent shipping service and the experience was excellent...',
    author: 'Khaled Al-Saeed',
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1cffeb4f4-1763294539510.png",
    authorImageAlt: 'Young Arab professional man with beard in white shirt working on laptop in modern workspace',
    replies: 12,
    views: 189,
    category: 'Express Shipping',
    timestamp: '1 day ago',
    isSolved: true
  },
  {
    id: 4,
    title: 'Tips to reduce shipping costs for e-commerce stores',
    excerpt: 'As an e-commerce store owner, shipping is a large part of costs. These are my strategies to reduce costs...',
    author: 'Sarah Hassan',
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf3d1d6a-1767858482849.png",
    authorImageAlt: 'Confident Arab businesswoman in professional attire presenting business strategy in modern office',
    replies: 31,
    views: 478,
    category: 'E-commerce',
    timestamp: '2 days ago',
    isSolved: false
  }];


  const handleSearchSubmit = (query: string) => {
    if (!isHydrated) return;
    console.log('Search query:', query);
  };

  const handleCardClick = (link: string) => {
    if (!isHydrated) return;
    console.log('Navigate to:', link);
  };

  const handleResourceClick = (url: string) => {
    if (!isHydrated) return;
    console.log('Open resource:', url);
  };

  const handleContactClick = (action: string) => {
    if (!isHydrated) return;
    console.log('Contact action:', action);
  };

  const handlePostClick = (postId: number) => {
    if (!isHydrated) return;
    console.log('Open post:', postId);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:319:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="319" data-component-end-line="330" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:320:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="320" data-component-end-line="329" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:321:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="321" data-component-end-line="321" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-primary%20h-96%22%7D" className="bg-primary h-96"></div>
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:322:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="322" data-component-end-line="328" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
            <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:323:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="323" data-component-end-line="327" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) =>
              <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:325:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="325" data-component-end-line="325" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20h-48%20rounded-xl%22%7D" key={i} className="bg-card h-48 rounded-xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:335:4" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="335" data-component-end-line="343" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HeroSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:336:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="336" data-component-end-line="336" data-component-file="SupportCenterInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" onSearchSubmit={handleSearchSubmit} />
      <QuickAccessCards data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:337:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="337" data-component-end-line="337" data-component-file="SupportCenterInteractive.tsx" data-component-name="QuickAccessCards" data-component-content="%7B%22elementName%22%3A%22QuickAccessCards%22%7D" cards={quickAccessCards} onCardClick={handleCardClick} />
      <FAQSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:338:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="338" data-component-end-line="338" data-component-file="SupportCenterInteractive.tsx" data-component-name="FAQSection" data-component-content="%7B%22elementName%22%3A%22FAQSection%22%7D" faqs={faqs} categories={faqCategories} />
      <ResourceLibrary data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:339:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="339" data-component-end-line="339" data-component-file="SupportCenterInteractive.tsx" data-component-name="ResourceLibrary" data-component-content="%7B%22elementName%22%3A%22ResourceLibrary%22%7D" resources={resources} onResourceClick={handleResourceClick} />
      <TroubleshootingWizard data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:340:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="340" data-component-end-line="340" data-component-file="SupportCenterInteractive.tsx" data-component-name="TroubleshootingWizard" data-component-content="%7B%22elementName%22%3A%22TroubleshootingWizard%22%7D" steps={wizardSteps} />
      <ContactOptions data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:341:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="341" data-component-end-line="341" data-component-file="SupportCenterInteractive.tsx" data-component-name="ContactOptions" data-component-content="%7B%22elementName%22%3A%22ContactOptions%22%7D" options={contactOptions} onContactClick={handleContactClick} />
      <CommunityForum data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:342:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="342" data-component-end-line="342" data-component-file="SupportCenterInteractive.tsx" data-component-name="CommunityForum" data-component-content="%7B%22elementName%22%3A%22CommunityForum%22%7D" posts={forumPosts} onPostClick={handlePostClick} />
    </div>);

};

export default SupportCenterInteractive;